import * as React from "react";
import classnames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tree, Popconfirm } from "antd";
import { observer } from "mobx-react";
import { TableLoader } from "Components/ScrollableTable/components/TableLoader";
import { I18N_NS } from "Config/i18n";
import { viewsFoldersStore, ExtendedDataNode } from "../Stores/ViewsFoldersStore";
import { Button } from "Components/General/Button";
import { withTranslation, WithTranslation } from "react-i18next";
import { WithModal, withModal } from "Components/General/Modal/withModal";
import { viewsStore } from "../Stores/ViewsStore";
import { AddNavigationForm } from "./AddNavigationForm";

interface ViewsSidebarProps extends WithTranslation, WithModal {
  onFolderSelect: (folderId: number) => void;
}

interface ViewsSidebarState {
  isOpen: boolean;
  height: number;
  width: number;
  currentlySelectedViewEntry: string;
}

export const ViewsSidebar = withTranslation(I18N_NS.PROJECT)(
  withModal(
    observer(
      class extends React.Component<ViewsSidebarProps, ViewsSidebarState> {
        constructor(props: ViewsSidebarProps) {
          super(props);

          this.state = {
            isOpen: false,
            height: null,
            width: null,
            currentlySelectedViewEntry: "",
          };
        }

        componentDidMount() {
          this.setSidebarHeight();
          this.makeResizableSidebar();
          window.addEventListener("resize", this.setSidebarHeight);
        }

        componentWillUnmount() {
          window.removeEventListener("resize", this.setSidebarHeight);
        }

        makeResizableSidebar = () => {
          const sidebar: HTMLDivElement = document.querySelector(".documents-sidebar");
          const resizer: HTMLDivElement = document.querySelector(".documents-sidebar-wrapper .resizable");

          const resize = (e) => {
            const size = e.pageX - sidebar.getBoundingClientRect().left;
            sidebar.setAttribute("style", `width: ${size}px; min-width: ${size}px`);
          };

          const stopResize = () => {
            const sidebarWidth = sidebar.style.width.split("px")[0];
            if (Number(sidebarWidth) < 250) {
              this.toggleSidebar();
            }
            window.removeEventListener("mousemove", resize);
            window.removeEventListener("mouseup", stopResize);
          };

          resizer.addEventListener("mousedown", (e: MouseEvent) => {
            e.preventDefault();
            window.addEventListener("mousemove", resize);
            window.addEventListener("mouseup", stopResize);
          });
        };

        setSidebarHeight = () => {
          const documentHeight: number = document.documentElement.clientHeight;
          const headerHeight: number = document.getElementsByTagName("header")[0].clientHeight;
          this.setState({
            height: documentHeight - headerHeight,
          });
        };

        setSidebarWidth = (isOpen: boolean) => {
          const sidebar: HTMLDivElement = document.querySelector(".documents-sidebar");
          let size: number;
          isOpen ? (size = 280) : (size = 15);
          sidebar.style.width = `${size}px`;
          sidebar.style.minWidth = `${size}px`;
        };

        toggleSidebar = () => {
          this.setState({ isOpen: !this.state.isOpen }, () => {
            this.setSidebarWidth(this.state.isOpen);
          });
        };

        handleFolderSelect = (selectedKeys) => {
          const folderKey = selectedKeys[0];
          const foundItem = viewsFoldersStore.getNavigationEntry(folderKey);
          // use custom events to trigger fetch request for files at View.tsx
          if (foundItem && foundItem.metadata) {
            const payload = {};
            Object.entries(foundItem.metadata).forEach(([key, value]) => {
              payload[`metadata_${key}`] = value;
            });
            const event = new CustomEvent("filter_view", {
              detail: payload,
            });
            document.dispatchEvent(event);
            this.setState({ currentlySelectedViewEntry: folderKey });
          } else {
            console.warn("Folder id not found in Views sidebar");
          }
        };

        handleLoad = async (node) => {
          try {
            console.log("node", node);
            if (node.children) {
              return await Promise.resolve();
            }
            await viewsFoldersStore.fetchFolder(node.key);
          } catch (error) {
            throw error;
          }
        };

        handleExpand = (expandedKeys, info) => {
          viewsFoldersStore.setExpandedKey(info.node.key, info.expanded);
        };

        get selectedKeys() {
          const { currentlySelectedViewEntry } = this.state;
          if (currentlySelectedViewEntry) {
            return [currentlySelectedViewEntry];
          }
          return [];
        }

        handleAddNavigation = () => {
          const { t, showModal } = this.props;
          showModal(AddNavigationForm, {
            modalProps: { title: t("New navigation"), footer: null, width: 680 },
            componentProps: {
              viewMetadata: viewsStore.viewInformation?.metadata,
              submit: viewsFoldersStore.createNavigation,
            },
          });
        };

        handleEditNavigation = (navigationId: number) => {
          const { t, showModal } = this.props;
          showModal(AddNavigationForm, {
            modalProps: { title: t("Edit navigation"), footer: null, width: 680 },
            componentProps: {
              initialData: viewsFoldersStore.dbData.find((item) => item.id === navigationId),
              viewMetadata: viewsStore.viewInformation?.metadata,
              submit: viewsFoldersStore.editNavigation,
            },
          });
        };

        handleDeleteNavigation = (navigationId: number) => {
          viewsFoldersStore.deleteNavigation(navigationId);
        };

        renderTitle = (node: ExtendedDataNode) => {
          if (!node.navigationId) {
            return <span>{this.props.t("View Navigation")}</span>;
          }
          if (node.isNavigation) {
            return (
              <span title={node.title.toString()}>
                {node.title as string}
                <span onClick={() => this.handleEditNavigation(node.navigationId)} className="navigation-title-icon">
                  <FontAwesomeIcon fixedWidth={true} icon={["far", "edit"]} />
                </span>
                <Popconfirm
                  title={this.props.t("Are you sure?")}
                  onConfirm={() => this.handleDeleteNavigation(node.navigationId)}
                  okButtonProps={{ loading: viewsFoldersStore.deleteState === "LOADING" }}
                >
                  <span className="navigation-title-icon">
                    <FontAwesomeIcon fixedWidth={true} icon={["far", "trash-alt"]} />
                  </span>
                </Popconfirm>
              </span>
            );
          }
          return <span title={node.title.toString()}>{node.title as string}</span>;
        };

        render() {
          const { t } = this.props;
          const { isOpen } = this.state;
          const sidebarClassName = classnames("documents-sidebar", { "is-open": isOpen });
          const toggleIconClassName = classnames("toggle-icon", { "is-open": isOpen });
          const toggleResizerClassName = classnames("resizable", { block: isOpen });
          const isNotEmpty = !!viewsFoldersStore.data.children?.length;

          return (
            <div className="documents-sidebar-wrapper" style={{ height: this.state.height }}>
              <div className={sidebarClassName} style={{ overflowY: "auto", overflowX: "hidden" }}>
                <TableLoader
                  loading={viewsFoldersStore.state === "LOADING" && !viewsFoldersStore.data}
                  style={{ backgroundColor: "#e0e5ed" }}
                />
                <Button
                  faIcon="plus"
                  className="ml-sm"
                  style={{ alignSelf: "flex-start" }}
                  size="small"
                  type="primary"
                  onClick={this.handleAddNavigation}
                >
                  {t("Navigation")}
                </Button>
                {isNotEmpty && (
                  <>
                    <Tree
                      blockNode={true}
                      // defaultExpandedKeys={viewsFoldersStore.expandedKeys}
                      // defaultSelectedKeys={[viewsFoldersStore.rootFolderId]}
                      expandedKeys={viewsFoldersStore.expandedKeys}
                      onSelect={this.handleFolderSelect}
                      onExpand={this.handleExpand}
                      selectedKeys={this.selectedKeys}
                      loadData={this.handleLoad}
                      style={{ background: "transparent", fontSize: 13 }}
                      treeData={viewsFoldersStore.treeData}
                      titleRender={this.renderTitle}
                    ></Tree>
                  </>
                )}
              </div>
              <div className={toggleResizerClassName}>
                <div className="line" />
              </div>
              <div className={toggleIconClassName} onClick={this.toggleSidebar}>
                <div className="toggle-background" />
                <FontAwesomeIcon icon={["far", "chevron-right"]} size={"lg"} className="documents-sidebar-icon" />
              </div>
            </div>
          );
        }
      }
    )
  )
);
